/**
 * M11: the layered deep-space background of the galaxy map.
 *
 *   far layer   – a tile of fine star dust, parallax 0.08 (barely moves → depth)
 *   near layer  – a tile of nebula gas, parallax 0.22
 *   world layer – large soft nebula clouds and distant star clusters, anchored in WORLD space on a fixed grid
 *
 * All of it is deterministic and independent of game data: the pattern comes from constant seeds and the grid cell
 * coordinates only, so the background can never reveal anything about systems the player does not know (fog of war).
 * Clouds are faint (≤ 0.14 alpha) and sit BELOW lanes, stars and labels so game information is never covered.
 * The tiles are baked once into textures; the world layer keeps a pool of sprites for the cells in view.
 */
import * as PIXI from 'pixi.js';
import { cellHash, mulberry32, visibleWorldRect } from '../graphics/lod.ts';
import type { ArtAtlas } from '../graphics/pixi-art.ts';
import { QUALITY_SETTINGS, type Quality } from '../graphics/quality.ts';

const NEBULA_COLORS = [0x2b4bb8, 0x5b2fb0, 0x0e6f94, 0x8a2f6e, 0x1f7a6a];
const CELL = 420;

function bakeDust(renderer: PIXI.Renderer, quality: Quality): PIXI.Texture {
  const size = quality === 'low' ? 384 : 512;
  const g = new PIXI.Graphics();
  g.rect(0, 0, size, size).fill({ color: 0x000000, alpha: 0.001 });
  const rng = mulberry32(0x5eed);
  const count = quality === 'low' ? 90 : 170;
  for (let i = 0; i < count; i++) {
    const x = 6 + rng() * (size - 12), y = 6 + rng() * (size - 12);
    const bright = rng();
    const tint = bright > 0.9 ? 0xbfdcff : bright < 0.12 ? 0xffd9b0 : 0xffffff;
    const r = 0.35 + rng() * (bright > 0.9 ? 1.1 : 0.6);
    if (bright > 0.8) g.circle(x, y, r * 3.4).fill({ color: tint, alpha: 0.05 });
    g.circle(x, y, r).fill({ color: tint, alpha: 0.2 + bright * 0.55 });
  }
  const t = renderer.generateTexture({ target: g, resolution: quality === 'low' ? 1 : 1.5 });
  g.destroy();
  return t;
}

function bakeNebulaTile(renderer: PIXI.Renderer): PIXI.Texture {
  const size = 768;
  const g = new PIXI.Graphics();
  g.rect(0, 0, size, size).fill({ color: 0x000000, alpha: 0.001 });
  const rng = mulberry32(0xc10d);
  for (let i = 0; i < 46; i++) {
    const cx = rng() * size, cy = rng() * size;
    const r = 70 + rng() * 210;
    const color = NEBULA_COLORS[Math.floor(rng() * NEBULA_COLORS.length)]!;
    // wrap around so the tile repeats seamlessly: draw the blob at the 4 edge offsets that touch the tile
    const offs = [[0, 0], [size, 0], [-size, 0], [0, size], [0, -size], [size, size], [-size, -size], [size, -size], [-size, size]];
    for (const [ox, oy] of offs) {
      if (cx + ox + r < 0 || cx + ox - r > size || cy + oy + r < 0 || cy + oy - r > size) continue;
      for (let k = 12; k >= 1; k--) {
        const t = k / 12;
        g.circle(cx + ox! + (rng() - 0.5) * 6, cy + oy! + (rng() - 0.5) * 6, r * t).fill({ color, alpha: 0.012 * (1.4 - t) });
      }
    }
  }
  const t = renderer.generateTexture({ target: g, resolution: 0.75 });
  g.destroy();
  return t;
}

function bakeCluster(renderer: PIXI.Renderer): PIXI.Texture {
  const g = new PIXI.Graphics();
  g.circle(0, 0, 64).fill({ color: 0x000000, alpha: 0.001 });
  const rng = mulberry32(0xc1a57e);
  for (let i = 0; i < 34; i++) {
    const a = rng() * Math.PI * 2, d = Math.pow(rng(), 1.7) * 52;
    const r = 0.5 + rng() * 1.3;
    g.circle(Math.cos(a) * d, Math.sin(a) * d, r * 2.4).fill({ color: 0xcfe3ff, alpha: 0.05 });
    g.circle(Math.cos(a) * d, Math.sin(a) * d, r).fill({ color: 0xeaf3ff, alpha: 0.4 + rng() * 0.5 });
  }
  const t = renderer.generateTexture({ target: g, resolution: 1.5 });
  g.destroy();
  return t;
}

export class MapBackdrop {
  readonly screenLayer = new PIXI.Container();
  readonly worldLayer = new PIXI.Container();
  private far: PIXI.TilingSprite;
  private near: PIXI.TilingSprite | null = null;
  private cells = new Map<string, PIXI.Sprite>();
  private textures: PIXI.Texture[] = [];
  private cluster: PIXI.Texture | null = null;
  private settings: ReturnType<typeof getSettings>;

  constructor(renderer: PIXI.Renderer, private atlas: ArtAtlas, quality: Quality, w: number, h: number) {
    this.settings = getSettings(quality);
    const dust = bakeDust(renderer, quality);
    this.textures.push(dust);
    this.far = new PIXI.TilingSprite({ texture: dust, width: w, height: h });
    this.screenLayer.addChild(this.far);
    if (this.settings.backgroundLayers === 2) {
      const neb = bakeNebulaTile(renderer);
      this.textures.push(neb);
      this.near = new PIXI.TilingSprite({ texture: neb, width: w, height: h });
      this.near.alpha = 0.9;
      this.near.tileScale.set(1 / 0.75);
      this.screenLayer.addChildAt(this.near, 0);
    }
    if (this.settings.nebulae > 0) this.cluster = bakeCluster(renderer);
    if (this.cluster) this.textures.push(this.cluster);
  }

  resize(w: number, h: number): void {
    this.far.width = w; this.far.height = h;
    if (this.near) { this.near.width = w; this.near.height = h; }
  }

  /** Called when the camera moved: parallax for the screen layers, cells for the world layer. */
  update(view: { x: number; y: number; scale: number }, w: number, h: number): void {
    this.far.tilePosition.set(view.x * 0.08, view.y * 0.08);
    if (this.near) {
      this.near.tilePosition.set(view.x * 0.22, view.y * 0.22);
      this.near.tileScale.set((1 / 0.75) * (0.85 + 0.15 * Math.min(1.6, view.scale)));
    }
    if (this.settings.nebulae <= 0) return;
    const r = visibleWorldRect(view, w, h);
    const i0 = Math.floor(r.x0 / CELL) - 1, i1 = Math.floor(r.x1 / CELL) + 1, j0 = Math.floor(r.y0 / CELL) - 1, j1 = Math.floor(r.y1 / CELL) + 1;
    // never more cells than the quality budget allows (zoomed far out)
    if ((i1 - i0 + 1) * (j1 - j0 + 1) > this.settings.nebulae * 6) { this.clearCells(); return; }
    const keep = new Set<string>();
    for (let i = i0; i <= i1; i++) {
      for (let j = j0; j <= j1; j++) {
        const h0 = cellHash(i, j, 1);
        if (h0 < 0.38) continue; // empty space
        const key = `${i},${j}`;
        keep.add(key);
        if (this.cells.has(key)) continue;
        const isCluster = h0 > 0.9 && this.cluster;
        const sprite = new PIXI.Sprite(isCluster ? this.cluster! : this.atlas.glow(64));
        sprite.anchor.set(0.5);
        sprite.position.set((i + cellHash(i, j, 2)) * CELL, (j + cellHash(i, j, 3)) * CELL);
        if (isCluster) {
          sprite.scale.set((0.9 + cellHash(i, j, 4)) * 1.1);
          sprite.alpha = 0.5;
        } else {
          sprite.tint = NEBULA_COLORS[Math.floor(cellHash(i, j, 5) * NEBULA_COLORS.length)]!;
          sprite.scale.set((CELL * (0.8 + cellHash(i, j, 6) * 1.1)) / 64);
          sprite.alpha = 0.05 + cellHash(i, j, 7) * 0.09;
        }
        this.cells.set(key, sprite);
        this.worldLayer.addChild(sprite);
      }
    }
    for (const [key, s] of this.cells) {
      if (!keep.has(key)) { this.worldLayer.removeChild(s); s.destroy(); this.cells.delete(key); }
    }
  }

  private clearCells(): void {
    for (const s of this.cells.values()) { this.worldLayer.removeChild(s); s.destroy(); }
    this.cells.clear();
  }

  get cellCount(): number { return this.cells.size; }

  destroy(): void {
    this.clearCells();
    this.screenLayer.destroy({ children: true });
    this.worldLayer.destroy({ children: true });
    for (const t of this.textures) t.destroy(true);
  }
}

function getSettings(q: Quality) { return QUALITY_SETTINGS[q]; }
